import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { Landmark, Building2, ExternalLink } from "lucide-react";

function RouteToggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative h-5 w-9 rounded-full border transition-colors shrink-0 ${checked ? "bg-accent border-accent" : "bg-surface border-border"}`}>
      <span className={`absolute top-[3px] left-[3px] h-3 w-3 rounded-full transition-transform ${checked ? "bg-black translate-x-4" : "bg-muted-foreground"}`} />
    </button>
  );
}

export default function FundingRoutePanel({ iid, onNavigate, onOpenFinancingCase, onChanged }) {
  const [d, setD] = useState(null);
  const [grantSel, setGrantSel] = useState("");
  const [offerSel, setOfferSel] = useState("");
  const [quantum, setQuantum] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/susfin/register/${iid}/route`).then((r) => {
    setD(r.data);
    setQuantum((q) => q || (r.data.default_quantum ? String(Math.round(r.data.default_quantum)) : ""));
  }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { if (iid) load(); }, [iid]); // eslint-disable-line

  if (!d) return <div className="border border-border rounded-sm p-4 text-xs text-muted-foreground font-mono">Loading funding route…</div>;

  const toggle = async (key, val) => {
    try {
      const r = await api.put(`/susfin/register/${iid}/route`, { [key]: val });
      setD(r.data); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const startGrant = async () => {
    setBusy("grant");
    try {
      const r = await api.post(`/susfin/register/${iid}/grant-application`, { grant_id: grantSel });
      toast.success(r.data.created
        ? `${r.data.application.code} application created in the Grants tracker`
        : `Linked the existing open ${r.data.application.code} application`);
      load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  const createCase = async () => {
    setBusy("bank");
    try {
      const offer = d.offer_options.find((o) => o.id === offerSel);
      const r = await api.post("/green-finance/cases", {
        case_name: `${offer?.name || "Financing"} for ${d.initiative_name}`,
        linked_offer_id: offerSel, linked_initiative_id: iid,
        financing_quantum_requested: Number(quantum || 0),
      });
      toast.success("Bank financing case created and linked");
      load(); onChanged?.();
      onOpenFinancingCase?.(r.data.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="border border-border rounded-sm p-4 space-y-4" data-testid="sf-route-panel">
      <div>
        <div className="label-eyebrow text-[10px]">Funding route</div>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          How will this investment be funded? Choose Grants and/or Bank Financing — each route links to its tracker.
        </p>
      </div>

      {/* Grants route */}
      <div className="border border-border/60 rounded-sm p-3 space-y-2" data-testid="sf-route-grants">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Landmark className="h-4 w-4 text-[#00E65B]" /> Grants
            {d.links.grant && (
              <Badge variant="outline" className="text-[9px] uppercase text-[#00E65B] border-[#00E65B]/40" data-testid="sf-route-grant-status">
                {d.links.grant.code} · {d.links.grant.status_label}
              </Badge>
            )}
          </div>
          <RouteToggle checked={d.funding_route.grants} onChange={(v) => toggle("grants", v)} testid="sf-route-grants-toggle" />
        </div>
        {d.funding_route.grants && (
          d.links.grant ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">
                {d.links.grant.name}{d.links.grant.claim_deadline ? ` · claim due ${d.links.grant.claim_deadline}` : ""}
              </span>
              <Button size="sm" variant="outline" className="h-7" onClick={() => onNavigate?.("grants")} data-testid="sf-route-grant-open">
                Open in Grants <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <Label className="text-[10px]">Scheme</Label>
                <Select value={grantSel} onValueChange={setGrantSel}>
                  <SelectTrigger className="h-8 text-xs" data-testid="sf-route-grant-select"><SelectValue placeholder="Select a grant" /></SelectTrigger>
                  <SelectContent>
                    {d.grant_options.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" variant="primary" className="h-8" disabled={!grantSel || busy === "grant"} onClick={startGrant} data-testid="sf-route-grant-start">
                {busy === "grant" ? "Starting…" : "Start application"}
              </Button>
            </div>
          )
        )}
      </div>

      {/* Bank financing route */}
      <div className="border border-border/60 rounded-sm p-3 space-y-2" data-testid="sf-route-bank">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Building2 className="h-4 w-4 text-[#00B4E6]" /> Bank Financing
            {d.links.bank && (
              <Badge variant="outline" className="text-[9px] uppercase text-[#00B4E6] border-[#00B4E6]/40" data-testid="sf-route-bank-status">
                {d.links.bank.status_label}
              </Badge>
            )}
          </div>
          <RouteToggle checked={d.funding_route.bank} onChange={(v) => toggle("bank", v)} testid="sf-route-bank-toggle" />
        </div>
        {d.funding_route.bank && (
          d.links.bank ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground truncate">
                {d.links.bank.name}{d.links.bank.quantum_requested ? ` · ${formatSgd(d.links.bank.quantum_requested)} requested` : ""}
              </span>
              <Button size="sm" variant="outline" className="h-7 shrink-0" onClick={() => onOpenFinancingCase?.(d.links.bank.case_id)} data-testid="sf-route-bank-open">
                Open case <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <Label className="text-[10px]">Bank product</Label>
                <Select value={offerSel} onValueChange={setOfferSel}>
                  <SelectTrigger className="h-8 text-xs" data-testid="sf-route-offer-select"><SelectValue placeholder="Select an offer" /></SelectTrigger>
                  <SelectContent>
                    {d.offer_options.map((o) => <SelectItem key={o.id} value={o.id}>{o.bank_name} — {o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label className="text-[10px]">Quantum (S$)</Label>
                <Input className="h-8 text-xs" type="text" inputMode="numeric" value={quantum}
                  onChange={(e) => setQuantum(e.target.value.replace(/[^0-9]/g, ""))} data-testid="sf-route-quantum" />
              </div>
              <Button size="sm" variant="primary" className="h-8" disabled={!offerSel || busy === "bank"} onClick={createCase} data-testid="sf-route-bank-create">
                {busy === "bank" ? "Creating…" : "Create financing case"}
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
}
